var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { resetPasswordApi } from "apis/eduidResetPassword";
import { InternalCaptcha } from "components/Common/Captcha";
import { useAppDispatch, useAppSelector } from "eduid-hooks";
import { useCallback, useEffect } from "react";
import { FormattedMessage } from "react-intl";
import { clearNotifications } from "slices/Notifications";
import resetPasswordSlice from "slices/ResetPassword";
import { ResetPasswordStepIndicator } from "./ResetPasswordStepIndicator";
export function ResetPasswordCaptcha() {
    const captcha = useAppSelector((state) => state.resetPassword.captcha);
    const captcha_completed = useAppSelector((state) => state.resetPassword.captcha_completed);
    const dashboard_link = useAppSelector((state) => state.config.dashboard_link);
    const dispatch = useAppDispatch();
    const [getCaptchaRequest] = resetPasswordApi.useLazyGetResetPasswordCaptchaRequestQuery();
    useEffect(() => {
        if ((captcha === null || captcha === void 0 ? void 0 : captcha.internal_response) || captcha_completed) {
            dispatch(resetPasswordSlice.actions.setNextPage("PROCESS_CAPTCHA"));
        }
    }, [captcha === null || captcha === void 0 ? void 0 : captcha.internal_response, captcha_completed, dispatch]);
    function getCaptcha() {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield getCaptchaRequest();
            if (response.isSuccess) {
                return response.data.payload;
            }
        });
    }
    function handleCaptchaCancel() {
        if (dashboard_link) {
            globalThis.location.href = dashboard_link;
        }
    }
    function handleCaptchaCompleted(response) {
        if (response) {
            dispatch(resetPasswordSlice.actions.setCaptchaResponse({ internal_response: response }));
            dispatch(resetPasswordSlice.actions.setNextPage("PROCESS_CAPTCHA"));
        }
    }
    const args = { handleCaptchaCancel, handleCaptchaCompleted };
    // If the user has already completed the captcha, don't show it again
    if (captcha_completed) {
        return null;
    }
    return (_jsxs("div", { className: "step-container", children: [_jsxs("section", { className: "intro", children: [_jsx("h1", { children: _jsx(FormattedMessage, { id: "captcha.reset", defaultMessage: "Reset Password: Confirm that you are a human", description: "Reset password captcha" }) }), _jsx("div", { className: "lead", children: _jsx("p", { children: _jsx(FormattedMessage, { id: "common.captchaLead", defaultMessage: "Confirm that you are a human as protection against automated spam.", description: "Reset password captcha lead text" }) }) }), _jsx(InternalCaptcha, Object.assign({}, args, { getCaptcha: getCaptcha }))] }), _jsx(ResetPasswordStepIndicator, { currentStep: 2 })] }));
}
export function ProcessCaptcha() {
    const captcha = useAppSelector((state) => state.resetPassword.captcha);
    const captcha_completed = useAppSelector((state) => state.resetPassword.captcha_completed);
    const email = useAppSelector((state) => state.resetPassword.email_address);
    const dispatch = useAppDispatch();
    const [sendCaptchaResponse] = resetPasswordApi.useLazySendResetPasswordCaptchaResponseQuery();
    const [requestEmailLink] = resetPasswordApi.useLazyRequestEmailLinkQuery();
    const [getResetPasswordState] = resetPasswordApi.useLazyGetResetPasswordStateQuery();
    const sendEmailLink = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        var _a, _b;
        if (email) {
            const response = yield requestEmailLink({ email });
            if (response.isSuccess) {
                // Refresh the status so the expiry countdown belongs to the code that was just sent.
                yield getResetPasswordState();
                dispatch(resetPasswordSlice.actions.setNextPage("EMAIL_LINK_SENT"));
            }
            else if (((_b = (_a = response.error) === null || _a === void 0 ? void 0 : _a.payload) === null || _b === void 0 ? void 0 : _b.message) !== "resetpw.email-code-too-many-tries") {
                // On a lockout the slice has already routed to RESET_PW_LOCKED - don't send the user
                // back to a form whose only action returns the same error.
                dispatch(resetPasswordSlice.actions.setNextPage("RESET_PW_ENTER_EMAIL"));
            }
        }
    }), [dispatch, email, requestEmailLink, getResetPasswordState]);
    const sendCaptcha = useCallback((captcha) => __awaiter(this, void 0, void 0, function* () {
        const response = yield sendCaptchaResponse(captcha);
        if (response.isSuccess) {
            dispatch(clearNotifications());
            sendEmailLink();
        }
        else {
            dispatch(resetPasswordSlice.actions.setNextPage("RESET_PW_CAPTCHA"));
        }
    }), [sendCaptchaResponse, dispatch, sendEmailLink]);
    useEffect(() => {
        if (captcha_completed) {
            sendEmailLink();
        }
    }, [captcha_completed, sendEmailLink]);
    useEffect(() => {
        if (captcha && !captcha_completed) {
            sendCaptcha(captcha);
        }
    }, [captcha, captcha_completed, sendCaptcha]);
    // Show a blank screen while we wait for a captcha response from the backend
    return null;
}
